import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Select, Spin } from 'antd';
import { PoweroffOutlined, SearchOutlined } from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import queryString from 'query-string';
import _ from 'lodash';
import { isMobileOnly } from 'react-device-detect';
import { userInfoVar } from 'src/apollo/cache';
import LeftMenu2 from 'src/components/LevelUp/LeftMenu/index2';
import LevelUpLibraryMobileHeader from 'src/components/common/LevelUpLibraryMobileHeader';
import { Carousel3 } from 'src/components/LevelUp/Carousel';
import { FAVORITE_BOOK } from 'src/operations/mutations/externalUserStudy';
import { EXTERNAL_STUDY_BOOK } from 'src/operations/queries/externalStudy';
import { GET_STUDENT_LUPUSER, GET_TEACHER_USER } from 'src/operations/queries/getUser';
import { getLixileScoreObject } from 'src/utils/index';

const { Option } = Select;

const LEVEL_TITLES = {
  1: 'Beginner',
  2: 'Early',
  3: 'Developing',
  4: 'Progressing',
  5: 'Confident',
  6: 'Advanced',
  7: 'Proficient',
  8: 'Fluent',
  9: 'Articulate',
  10: 'Accomplished',
  11: 'Distinguished',
  12: 'Mastery',
};

const RowFlex = styled.div`
  display: flex;
  flex-direction: row;
`;

const Wrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  height: 100vh;
  padding-top: 64px;
  overflow: hidden;
  box-sizing: border-box;
  background:
    radial-gradient(ellipse 900px 520px at 15% 30%, rgba(34, 211, 238, 0.08) 0%, transparent 70%),
    radial-gradient(ellipse 700px 420px at 85% 70%, rgba(16, 185, 129, 0.06) 0%, transparent 70%),
    #e8f4f8;
`;

const MobileHeader = styled(RowFlex)`
  justify-content: space-between;
  align-items: center;
  height: 64px;
  padding: 0 12px;
  background-color: #ffffff;
  border-bottom: 1px solid rgba(8, 145, 178, 0.1);
`;

const MobileSpacer = styled.div`
  width: 34px;
  height: 34px;
`;

const MobileIconButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: none;
  background: rgba(8, 145, 178, 0.1);
  color: #16576b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
`;

const MobileKeywordBar = styled.div`
  padding: 8px 12px 0;
  background: #ffffff;
`;

const KeywordChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 20px;
  background: rgba(8, 145, 178, 0.08);
  border: 1.5px solid rgba(8, 145, 178, 0.12);
  color: #16576b;
  font-size: 13px;
  font-weight: 700;
`;

const MainWrapper = styled.div`
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  min-height: calc(100vh - 64px);
  height: calc(100vh - 64px);
  width: 100%;
  overflow: hidden;
`;

const Main = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  padding: ${({ $isMobile }) => ($isMobile ? '12px' : '12px 24px 20px')};
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
  box-sizing: border-box;
`;

const Hero = styled.div`
  display: flex;
  align-items: stretch;
  gap: 18px;
  padding: 22px 24px;
  border-radius: 26px;
  background: linear-gradient(135deg, #0891b2 0%, #14b8a6 100%);
  box-shadow: 0 10px 24px rgba(8, 145, 178, 0.18);
  color: #ffffff;
  flex-shrink: 0;
  @media (max-width: 768px) {
    padding: 18px;
    gap: 14px;
  }
`;

const HeroBadge = styled.div`
  min-width: 110px;
  padding: 12px 18px;
  border-radius: 18px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  backdrop-filter: blur(6px);
`;

const HeroBadgeNum = styled.div`
  font-family: 'Baloo 2', sans-serif;
  font-size: 30px;
  font-weight: 900;
  line-height: 1;
`;

const HeroBadgeLabel = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
  margin-top: 4px;
`;

const HeroInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeroEyebrow = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
  margin-bottom: 8px;
`;

const HeroTitle = styled.div`
  font-family: 'Baloo 2', sans-serif;
  font-size: 24px;
  font-weight: 900;
  line-height: 1.15;
  margin-bottom: 8px;
`;

const HeroSub = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.86);
  margin-bottom: 10px;
`;

const HeroChips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const HeroChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.28);
  font-size: 11px;
  font-weight: 800;
`;

const HeroStats = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  min-width: 0;
  justify-content: center;
  align-self: stretch;
  @media (max-width: 1100px) {
    width: 100%;
  }
`;

const HeroStat = styled.div`
  flex: 1 1 0;
  min-width: 132px;
  padding: 12px 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const HeroStatValue = styled.div`
  font-family: 'Baloo 2', sans-serif;
  font-size: 22px;
  font-weight: 900;
  line-height: 1;
`;

const HeroStatLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
  margin-top: 3px;
`;

const HeroStatHint = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 8px;
  line-height: 1.2;
`;

const ControlBar = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  border-radius: 22px;
  background: #ffffff;
  border: 1.5px solid rgba(8, 145, 178, 0.12);
  box-shadow: 0 2px 12px rgba(8, 145, 178, 0.12);
  flex-shrink: 0;
  @media (max-width: 1100px) {
    flex-wrap: wrap;
  }
`;

const GenreRail = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const GenreScroll = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  flex: 1;
  min-width: 0;
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(8, 145, 178, 0.22);
    border-radius: 999px;
  }
`;

const GenreArrowButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid rgba(8, 145, 178, 0.14);
  background: #ffffff;
  color: #0891b2;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.1);
  &:hover {
    background: #0891b2;
    color: #ffffff;
    border-color: #0891b2;
  }
`;

const GenreTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 20px;
  border: 1.5px solid ${(props) => (props.$active ? 'rgba(8,145,178,.28)' : 'rgba(8,145,178,.12)')};
  background: ${(props) => (props.$active ? 'rgba(8,145,178,.08)' : '#ffffff')};
  color: ${(props) => (props.$active ? '#0891b2' : '#4a6380')};
  font-size: 12px;
  font-weight: ${(props) => (props.$active ? 800 : 700)};
  white-space: nowrap;
  cursor: pointer;
`;

const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  border: 1.5px solid ${(props) => (props.$active ? 'rgba(249,115,22,.25)' : 'rgba(8,145,178,.12)')};
  background: ${(props) => (props.$active ? 'rgba(249,115,22,.12)' : '#ffffff')};
  color: ${(props) => (props.$active ? '#f97316' : '#4a6380')};
  cursor: pointer;
`;

const SortSelect = styled(Select)`
  width: 150px;
  .ant-select-selector {
    height: 38px !important;
    border-radius: 20px !important;
    border: 1.5px solid rgba(8, 145, 178, 0.14) !important;
    display: flex;
    align-items: center;
    box-shadow: none !important;
  }
`;

const SearchKeywordBox = styled.div`
  min-width: 200px;
  height: 38px;
  padding: 0 14px;
  border-radius: 20px;
  border: 1.5px solid rgba(8, 145, 178, 0.14);
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #4a6380;
  font-size: 12px;
  font-weight: 700;
`;

const BookArea = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(8, 145, 178, 0.06);
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(8, 145, 178, 0.62), rgba(6, 182, 212, 0.52));
    border-radius: 999px;
  }
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px 12px;
  color: #4a6380;
  font-size: 13px;
  font-weight: 700;
`;

const BookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  padding-bottom: 8px;
  @media (max-width: 1500px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const BookCard = styled.button`
  border: 1.5px solid rgba(8, 145, 178, 0.12);
  background: #ffffff;
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.22s ease;
  box-shadow: 0 4px 18px rgba(8, 145, 178, 0.12);
  padding: 0;
  text-align: left;
  display: flex;
  flex-direction: column;
  &:hover {
    transform: translateY(-6px) scale(1.02);
    border-color: #0891b2;
    box-shadow: 0 10px 28px rgba(8, 145, 178, 0.2);
  }
`;

const BookThumb = styled.div`
  width: 100%;
  aspect-ratio: 3 / 4;
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, #dff5fb 0%, #c9ebf5 100%);
`;

const BookImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BookOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent 40%, rgba(0, 0, 0, 0.55) 100%);
`;

const BookHeart = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${(props) => (props.$active ? '#fee2e2' : 'rgba(255,255,255,.92)')};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  cursor: pointer;
  z-index: 2;
`;

const BookDone = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-8deg);
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 3px solid #10b981;
  background: rgba(16, 185, 129, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 7px;
  font-weight: 900;
  color: #ffffff;
  box-shadow: 0 4px 20px rgba(16, 185, 129, 0.5);
  z-index: 3;
`;

const BookLevel = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 3px 9px;
  border-radius: 20px;
  background: rgba(8, 145, 178, 0.88);
  color: #ffffff;
  backdrop-filter: blur(4px);
`;

const BookBody = styled.div`
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BookTitle = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #172554;
  text-align: center;
  line-height: 1.4;
  min-height: 34px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BookAuthor = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #8fa3bb;
  text-align: center;
`;

const EmptyBox = styled.div`
  min-height: 240px;
  border-radius: 24px;
  border: 1.5px dashed rgba(8, 145, 178, 0.18);
  background: rgba(255, 255, 255, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-family: 'Baloo 2', sans-serif;
  font-size: 24px;
  font-weight: 700;
`;

export default function LevelUpSearch() {
  const history = useHistory();
  const location = useLocation();
  const isMobileView = window.isNativeAppMobile || isMobileOnly;
  const genreScrollRef = useRef(null);
  const { keyword = '' } = queryString.parse(location.search);
  const searchKeyword = typeof keyword === 'string' ? keyword.trim() : '';
  const [level, setLevel] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [sortby, setSortby] = useState('lowlevel');
  const [showDoneOnly, setShowDoneOnly] = useState(false);
  const [showFavOnly, setShowFavOnly] = useState(false);
  const [currentLevel, setCurrentLevel] = useState(null);

  let getUserType = GET_STUDENT_LUPUSER;
  if (userInfoVar() && userInfoVar()?.type !== '0' && userInfoVar()?.type !== 0) {
    getUserType = GET_TEACHER_USER;
  }

  useQuery(getUserType, {
    variables: {},
    onCompleted: (data) => {
      if (!data?.getUserData) return;
      let tempData = data.getUserData;
      if (tempData.class_teacher) {
        tempData = Object.assign({}, tempData, { classes: tempData.class_teacher.map((item) => item.class) });
      } else {
        tempData = Object.assign({}, tempData, { classes: tempData.class_student.map((item) => item.class) });
      }

      if (tempData?.user_levelup_tests?.length > 0) {
        const sortedTests = _.orderBy(
          tempData.user_levelup_tests.filter((item) => item.level),
          ['test_end_date'],
          ['desc'],
        );
        const latestTest = sortedTests[0];
        if (latestTest?.lexile && latestTest?.level) {
          setCurrentLevel(latestTest.level);
          return;
        }
      }

      setCurrentLevel(tempData?.classes?.[0]?.levelup_level || '');
    },
  });

  const { data: userBook, loading, refetch } = useQuery(EXTERNAL_STUDY_BOOK, {
    variables: {
      type: 'levelup',
      q: searchKeyword,
      level: null,
    },
    skip: searchKeyword.length < 2,
    fetchPolicy: 'no-cache',
  });

  const [favBook] = useMutation(FAVORITE_BOOK, {
    onCompleted: () => {
      refetch();
    },
  });

  const setLexileValue = (lexile = '') => {
    if (lexile.includes('BR')) return 0;
    if (lexile.includes('NP')) return 1000;
    return parseInt(lexile, 10);
  };

  const books = useMemo(() => {
    return (userBook?.getExternalUserBook || []).map((item, index) => {
      const lexileVal = !_.isNil(item?.lexile_val) ? item.lexile_val : setLexileValue(item?.lexile || '');
      const mxObj = getLixileScoreObject(lexileVal, 'A-1');
      const normalizedLevel =
        item?.mx_level !== undefined
          ? item?.mx_level === 'PH'
            ? parseInt(item?.is_interactive || 0, 10) === 0
              ? 0
              : -1
            : parseInt(item.mx_level, 10)
          : mxObj?.mx;
      return {
        ...item,
        no: index + 1,
        lexileVal,
        mxObj,
        level: normalizedLevel,
      };
    });
  }, [userBook]);

  useEffect(() => {
    setSelectedTopic('all');
  }, [level, searchKeyword]);

  const levelFilteredBooks = useMemo(() => {
    if (typeof level !== 'number') {
      return books;
    }
    return books.filter((item) => item.level === level);
  }, [books, level]);

  const topics = useMemo(() => {
    return [{ title: 'All', value: 'all' }].concat(
      levelFilteredBooks
        .map((item) => item.category1)
        .filter(Boolean)
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((value) => ({ title: value, value })),
    );
  }, [levelFilteredBooks]);

  const filteredBooks = useMemo(() => {
    let list = levelFilteredBooks.filter((item) => {
      if (selectedTopic === 'all') return true;
      return item.category1 === selectedTopic || item.category2 === selectedTopic || item.category3 === selectedTopic;
    });

    if (showDoneOnly) {
      list = list.filter((item) => item.is_completed);
    }
    if (showFavOnly) {
      list = list.filter((item) => item.is_fav);
    }

    if (sortby === 'title') list = _.sortBy(list, 'title');
    if (sortby === 'lowlevel') list = _.sortBy(list, 'lexile_val');
    if (sortby === 'highlevel') list = _.sortBy(list, 'lexile_val').reverse();
    if (sortby === 'fiction') list = list.filter((item) => item.genre === 'Fiction');
    if (sortby === 'nonfiction') list = list.filter((item) => item.genre === 'Non-Fiction');

    return list;
  }, [levelFilteredBooks, selectedTopic, showDoneOnly, showFavOnly, sortby]);

  const handleToggleFavorite = async (event, book) => {
    event.preventDefault();
    event.stopPropagation();
    await favBook({
      variables: {
        book_id: book?.book_no,
        type: 'levelup',
      },
    });
  };

  const handleGenreScroll = (direction) => {
    if (!genreScrollRef.current) return;
    genreScrollRef.current.scrollBy({
      left: direction * 220,
      behavior: 'smooth',
    });
  };

  const formatBookLevelLabel = (book) => {
    const levelLabel = book.level === 0 || book.mx_level === 'PH' ? 'mx PH' : `mx ${String(book.level).padStart(2, '0')}`;
    const lexileLabel = book.lexileVal < 0 ? `BR${Math.abs(book.lexileVal)}L` : `${book.lexileVal}L`;
    return `${levelLabel}(${lexileLabel})`;
  };

  const currentLevelLabel = useMemo(() => {
    if (level === -1) return 'Phonics Class';
    if (level === 0) return 'Phonics Readers';
    if (level >= 1 && level <= 12) return `mx ${String(level).padStart(2, '0')} · ${LEVEL_TITLES[level]} Reader`;
    return 'All Levels';
  }, [level]);

  const levelBadgeValue = useMemo(() => {
    if (level === -1 || level === 0) return 'PH';
    if (typeof level === 'number') return String(level).padStart(2, '0');
    return 'ALL';
  }, [level]);

  const desktopContent = (
    <MainWrapper $isMobile={false}>
      <LeftMenu2
        level={level}
        setLevel={setLevel}
        isSearch
        myLevel={parseInt(userInfoVar()?.type || 0, 10) !== 0 ? -2 : currentLevel === 'PH' ? 0 : parseInt(currentLevel, 10)}
        isStudent={parseInt(userInfoVar()?.type || 0, 10) === 0}
      />
      <Main $isMobile={false}>
        <Hero>
          <HeroBadge>
            <HeroBadgeNum>{levelBadgeValue}</HeroBadgeNum>
            <HeroBadgeLabel>{typeof level === 'number' ? 'mx level' : 'search'}</HeroBadgeLabel>
          </HeroBadge>
          <HeroInfo>
            <HeroEyebrow>Search Results</HeroEyebrow>
            <HeroTitle>{currentLevelLabel}</HeroTitle>
            <HeroSub>{searchKeyword ? `Keyword: "${searchKeyword}"` : 'Search from the header to find books.'}</HeroSub>
            <HeroChips>
              <HeroChip>📚 {`${filteredBooks.length} Books`}</HeroChip>
              <HeroChip>✅ {`${filteredBooks.filter((item) => item.is_completed).length} Completed`}</HeroChip>
              <HeroChip>🤍 {`${filteredBooks.filter((item) => item.is_fav).length} Favorite`}</HeroChip>
            </HeroChips>
          </HeroInfo>
          <HeroStats>
            <HeroStat>
              <HeroStatValue>{books.length}</HeroStatValue>
              <HeroStatLabel>Total Results</HeroStatLabel>
              <HeroStatHint>matched by keyword</HeroStatHint>
            </HeroStat>
            <HeroStat>
              <HeroStatValue>{typeof level === 'number' ? filteredBooks.length : topics.length - 1}</HeroStatValue>
              <HeroStatLabel>{typeof level === 'number' ? 'Filtered' : 'Topics'}</HeroStatLabel>
              <HeroStatHint>{typeof level === 'number' ? 'books in selected level' : 'available categories'}</HeroStatHint>
            </HeroStat>
          </HeroStats>
        </Hero>

        <ControlBar>
          <GenreRail>
            <GenreArrowButton type="button" onClick={() => handleGenreScroll(-1)}>
              ‹
            </GenreArrowButton>
            <GenreScroll ref={genreScrollRef}>
              {topics.map((topic) => (
                <GenreTab key={topic.value} type="button" $active={selectedTopic === topic.value} onClick={() => setSelectedTopic(topic.value)}>
                  <span>{topic.title}</span>
                </GenreTab>
              ))}
            </GenreScroll>
            <GenreArrowButton type="button" onClick={() => handleGenreScroll(1)}>
              ›
            </GenreArrowButton>
          </GenreRail>
          <FilterChip type="button" $active={showDoneOnly} onClick={() => setShowDoneOnly((prev) => !prev)}>
            ✅ Completed
          </FilterChip>
          <FilterChip type="button" $active={showFavOnly} onClick={() => setShowFavOnly((prev) => !prev)}>
            🤍 Favorite
          </FilterChip>
          <SortSelect value={sortby} onChange={setSortby} bordered={false}>
            <Option value="lowlevel">Lowest</Option>
            <Option value="highlevel">Highest</Option>
            <Option value="title">A to Z</Option>
            <Option value="fiction">Fiction</Option>
            <Option value="nonfiction">Non-Fiction</Option>
          </SortSelect>
          <SearchKeywordBox>
            <SearchOutlined style={{ color: '#64748b' }} />
            <span>{searchKeyword || 'No keyword'}</span>
          </SearchKeywordBox>
        </ControlBar>

        <BookArea>
          <ResultRow>
            <span>{`${filteredBooks.length} books found`}</span>
            <span>{typeof level === 'number' ? 'Click the same level again to clear the filter.' : 'Select a level from the left menu to filter.'}</span>
          </ResultRow>
          {searchKeyword.length < 2 ? (
            <EmptyBox>Type at least 2 characters in the header search.</EmptyBox>
          ) : loading ? (
            <EmptyBox>
              <Spin />
            </EmptyBox>
          ) : filteredBooks.length ? (
            <BookGrid>
              {filteredBooks.map((book) => (
                <BookCard
                  key={`${book.book_no}-${book.code}`}
                  type="button"
                  onClick={() =>
                    history.push({
                      pathname:
                        userInfoVar()?.type > 0 && userInfoVar()?.company_name !== 'gnb'
                          ? `/level-up/description/${book.code}`
                          : `/level-up/${book?.is_voca ? 'study' : `${book?.is_interactive ? 'epub-' : ''}reader`}/${book.code}`,
                      state:
                        !book?.selfStudy && book?.assigned_idx
                          ? { bookData: { ...book, assignedIdx: book?.assigned_idx }, assigned_idx: book?.assigned_idx }
                          : { bookData: book, assigned_idx: null },
                    })
                  }
                >
                  <BookThumb>
                    <BookImg src={`https://cdn.cloubot.com/LEVELUP/covers/${book?.code}.png`} alt={book?.title} />
                    <BookOverlay />
                    <BookHeart type="button" $active={!!book.is_fav} onClick={(event) => handleToggleFavorite(event, book)}>
                      {book.is_fav ? '❤️' : '🤍'}
                    </BookHeart>
                    {book.is_completed ? (
                      <BookDone>
                        <span style={{ fontSize: 22, lineHeight: 1 }}>✓</span>
                        COMPLETED
                      </BookDone>
                    ) : null}
                    <BookLevel>{formatBookLevelLabel(book)}</BookLevel>
                  </BookThumb>
                  <BookBody>
                    <BookTitle>{book.title}</BookTitle>
                    <BookAuthor>{book.author}</BookAuthor>
                  </BookBody>
                </BookCard>
              ))}
            </BookGrid>
          ) : (
            <EmptyBox>No books found for this filter.</EmptyBox>
          )}
        </BookArea>
      </Main>
    </MainWrapper>
  );

  const mobileContent = (
    <>
      <MobileHeader>
        <MobileSpacer />
        <div
          onClick={() => {
            history.push('/level-up');
          }}
          style={{ cursor: 'pointer' }}
        >
          <img src="/images/level_up_logo.svg" style={{ width: '138px', height: '30px' }} alt="level up logo" />
        </div>
        <RowFlex style={{ gap: 8 }}>
          <MobileIconButton type="button">
            <SearchOutlined />
          </MobileIconButton>
          <MobileIconButton type="button" onClick={() => history.push('/')}>
            <PoweroffOutlined />
          </MobileIconButton>
        </RowFlex>
      </MobileHeader>
      <MobileKeywordBar>
        <KeywordChip>
          <SearchOutlined />
          <span>{searchKeyword || 'No keyword'}</span>
        </KeywordChip>
      </MobileKeywordBar>
      <LevelUpLibraryMobileHeader currentMenu="bookshelf" />
      <MainWrapper $isMobile>
        <Main $isMobile>
          <Hero>
            <HeroBadge>
              <HeroBadgeNum>{levelBadgeValue}</HeroBadgeNum>
              <HeroBadgeLabel>{typeof level === 'number' ? 'mx level' : 'search'}</HeroBadgeLabel>
            </HeroBadge>
            <HeroInfo>
              <HeroEyebrow>Search Results</HeroEyebrow>
              <HeroTitle>{currentLevelLabel}</HeroTitle>
            </HeroInfo>
          </Hero>
          {searchKeyword.length < 2 ? (
            <EmptyBox>Type at least 2 characters in the header search.</EmptyBox>
          ) : loading ? (
            <EmptyBox>
              <Spin />
            </EmptyBox>
          ) : filteredBooks.length ? (
            <Carousel3 books={filteredBooks} handleFav={refetch} />
          ) : (
            <EmptyBox>No books found for this filter.</EmptyBox>
          )}
        </Main>
      </MainWrapper>
    </>
  );

  return <Wrapper>{isMobileView ? mobileContent : desktopContent}</Wrapper>;
}
